<template>
  <section class="list-select">
    <header class="list-select__title">{{ title }}</header>
    <ul class="list-select__list" ref="list">
      <template v-for="group in localGroups">
        <li :key="group.id" v-if="group.renderTitle" class="list-select__group">{{ group.title }}</li>
        <li v-for="item in filterItemsInGroup(group.id)"
            :key="item[keyId]"
            :data-key="item[keyId]"
            :class="{
            'list-select__item': true, 
            'active' : item[keyId] === computedValue 
          }"
            @click="computedValue = item[keyId]"
        >
          <span v-html="markValue(item[keyText])"></span>
        </li>
      </template>
    </ul>
  </section>
</template>

<script>
import uniq from "lodash/uniq";

export default {
  props: {
    title: String,
    items: Array,
    keyId: String,
    keyText: String,
    keyGroupId: String,
    groupTitleByIdFn: Function,
    sortGroupsFn: Function,
    sortItemsInGroupFn: Function,
    renderGroupTitleFn: {
      type: Function,
      default: () => true,
    },
    value: {},
    modelValue: {},
    highlightSubstring: String,
  },
  data() {
    return {
      localValue: null,
      localItems: null,
      localGroups: null,
      useGroups: false,
      highlightRegex: null,
      filterItemsPredicate: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      const listItem = this.$refs.list.querySelector(`[data-key='${this.localValue}']`);
      if (listItem) {
        listItem.scrollIntoView({block: "center", behavior: "smooth"});
      }
    });
  },
  watch: {
    value: {
      handler() {
        if (this.modelValue === undefined) {
          this.localValue = this.value;
        }
      },
      immediate: true,
    },
    modelValue: {
      handler() {
        if (this.modelValue !== undefined) {
          this.localValue = this.modelValue;
        }
      },
      immediate: true,
    },
    items: {
      handler() {
        this.localItems = this.items;

        const useGroups = !!this.keyGroupId && !!this.groupTitleByIdFn;
        if (useGroups) {
          const groupIds = uniq(this.localItems.map(item => item[this.keyGroupId]));

          if (!!this.sortGroupsFn) {
            groupIds.sort((a, b) => this.sortGroupsFn(a, b));
          }

          this.localGroups = groupIds.map(id => ({
            id: id,
            title: this.groupTitleByIdFn(id),
            renderTitle: this.renderGroupTitleFn(id),
          }));

          this.filterItemsPredicate = (item, groupId) => item[this.keyGroupId] === groupId;
        } else {
          this.localGroups = [{
            id: 0,
            title: "not important",
            renderTitle: false,
          }];
          this.filterItemsPredicate = item => true;
        }
      },
      immediate: true,
    },
    highlightSubstring: {
      handler() {
        if (this.highlightSubstring && this.highlightSubstring.length > 0) {
          this.highlightRegex = new RegExp(this.highlightSubstring, "gi");
        } else {
          this.highlightRegex = null;
        }
      },
      immediate: true,
    }
  },
  computed: {
    computedValue: {
      get() {
        return this.localValue;
      },
      set(v) {
        this.localValue = v;
        this.$emit("update:modelValue", v);
        this.$emit("update:value", v);
      }
    },
  },
  methods: {
    markValue(value) {
      if (this.highlightRegex) {
        return value.replace(this.highlightRegex, `<mark>$&</mark>`)
      }
      return value;
    },

    filterItemsInGroup(groupId) {
      const items = this.items.filter(item => this.filterItemsPredicate(item, groupId));
      if (this.sortItemsInGroupFn) {
        items.sort((a, b) => this.sortItemsInGroupFn(a, b));
      }

      return items;
    },
  },
};
</script>

<style lang="scss" scoped>


.list-select {
  overflow-y: auto;
  // firefox custom scroll
  //scrollbar-color: var(--main-10) var(--main-40); // ломает chrome
  //scrollbar-width: thin;                          // ломает chrome
  overscroll-behavior: none;
  /* todo 10450 Технически его можно добавлять через ResizeObserver */
  padding-inline-end: 10px; // чтобы элементы списка не касались скрола. Не если скрола нет, то это лишний паддинг. 

  &::-webkit-scrollbar {
    width: 5px;
    height: 5px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: var(--main-40);
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: var(--main-10);
  }

  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: var(--main-10);
  }
}

.list-select__title {
  position: sticky;
  inset-block-start: 0;
  font-weight: 600;
  border-bottom: 2px solid var(--main-40);
  padding-bottom: 3px;
  background: #fff;
}

.list-select__list {
  margin-bottom: 0;
  list-style-type: none;
  margin-top: 10px;
}

.list-select__group {
  height: 30px;
  line-height: 30px;
  border-bottom: 1px solid var(--main-40);
  margin-block: 10px;
  font-weight: 600;
}

.list-select__item {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  padding: 2px 4px;
  cursor: pointer;
  border-radius: var(--border-radius-sm);

  & + .list-select__item {
    margin-top: 5px;
  }

  &.active {
    background-color: var(--main-10);
    color: var(--white);
  }

  &:hover:not(.active) {
    background-color: var(--main-40);
  }

}
</style>

<style lang="scss">


.list-select__item {
  mark {
    color: var(--system-10);
    font-weight: 600;
    padding: 0;
    background-color: inherit;
  }
}
</style>
